import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getLead, updateLead, addNote } from '../../lib/api';
import { LEAD_STATUSES, LEAD_SOURCES, GYM_CONFIG, PREFERRED_CONTACTS } from '../../config';
import { useAuth } from '../../contexts/AuthContext';
import { gvCall, gvText } from '../../utils/googleVoice';
import { formatPhone } from '../../utils/phone';
import {
  ArrowLeft, Phone, Mail, Calendar, Clock, Loader2,
  MessageSquare, User, ChevronRight, Tag, LogOut, ShieldOff, ShieldCheck
} from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Separator } from '../../components/ui/separator';
import { toast } from 'sonner';
import TimeQuickSelect from '../../components/staff/TimeQuickSelect';

function StatusBadge({ status }) {
  const found = LEAD_STATUSES.find((s) => s.value === status);
  const colorClass = found?.color || 'bg-gray-50 text-gray-900 border-gray-200';
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded text-xs font-medium border ${colorClass}`}>
      {status}
    </span>
  );
}

const SOURCE_LABELS = {
  website_form: 'Website Form',
  book_a_tour: 'Book a Tour',
  book_a_visit: 'Book a Visit',
  contact_page: 'Contact Page',
  personal_training_inquiry: 'PT Inquiry',
  manual_entry: 'Manual Entry',
  csv_import: 'CSV Import',
  walk_in: 'Walk-In',
};

function formatTime12(time24) {
  if (!time24) return '';
  const [h, m] = time24.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h > 12 ? h - 12 : h === 0 ? 12 : h;
  return `${h12}:${String(m).padStart(2, '0')} ${period}`;
}

export default function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [followUpDate, setFollowUpDate] = useState('');
  const [followUpTime, setFollowUpTime] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [statusValue, setStatusValue] = useState('');

  const fetchLead = async () => {
    try {
      const res = await getLead(id);
      setLead(res.data);
      setEditNotes(res.data.notes || '');
      setStatusValue(res.data.status);
      setFollowUpDate(res.data.next_follow_up_date ? res.data.next_follow_up_date.split('T')[0] : '');
      setFollowUpTime(res.data.next_follow_up_time || '');
    } catch {
      toast.error('Failed to load lead');
      navigate('/demo/dashboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLead();
  }, [id]);

  const handleStatusChange = async (newStatus) => {
    setStatusValue(newStatus);
    setSaving(true);
    try {
      const updated = await updateLead(id, { status: newStatus });
      setLead(updated.data);
      toast.success(`Status updated to "${newStatus}"`);
    } catch {
      toast.error('Failed to update status');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveNotes = async () => {
    setSaving(true);
    try {
      await updateLead(id, { notes: editNotes });
      toast.success('Notes saved');
    } catch {
      toast.error('Failed to save notes');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveFollowUp = async () => {
    setSaving(true);
    try {
      await updateLead(id, { next_follow_up_date: followUpDate || null, next_follow_up_time: followUpTime || null });
      toast.success('Follow-up saved');
      await fetchLead();
    } catch {
      toast.error('Failed to save follow-up');
    } finally { setSaving(false); }
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    setAddingNote(true);
    try {
      await addNote(id, noteText);
      setNoteText('');
      toast.success('Note added');
      await fetchLead();
    } catch {
      toast.error('Failed to add note');
    } finally {
      setAddingNote(false);
    }
  };

  const handleToggleBlacklist = async () => {
    try {
      const token = localStorage.getItem('scs_token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const res = await fetch(`${backendUrl}/api/staff/leads/${id}/blacklist`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await res.json();
      toast.success(data.message || 'Blacklist updated');
      await fetchLead();
    } catch { toast.error('Failed to update blacklist'); }
  };

  const inputClass = 'w-full bg-gray-50 border border-gray-200 text-gray-900 placeholder:text-gray-600 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-300 transition-colors duration-200';

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#FF2E00] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!lead) return null;

  const activityLog = [...(lead.activity_log || [])].reverse();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-gray-50/95 backdrop-blur border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <button
              onClick={() => navigate('/demo/dashboard')}
              className="text-gray-700 hover:text-gray-900 flex items-center gap-1.5 text-sm transition-colors duration-200 shrink-0"
            >
              <ArrowLeft size={14} /> Dashboard
            </button>
            <span className="text-gray-500 shrink-0">/</span>
            <span className="text-gray-900 text-sm truncate">
              {lead.first_name} {lead.last_name}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {saving && <Loader2 size={14} className="text-gray-700 animate-spin" />}
            {/* Back to website */}
            <Link
              to="/"
              className="flex items-center gap-1 text-gray-700 hover:text-gray-900 text-xs font-medium border border-gray-200 hover:border-gray-400 px-2.5 py-1.5 rounded-md transition-colors duration-200"
            >
              <ArrowLeft size={11} />
              <span>Website</span>
            </Link>
            <button onClick={() => { logout(); navigate('/staff/login'); }} className="text-gray-700 hover:text-gray-900 p-1.5 rounded">
              <LogOut size={14} />
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Mobile: Tabs layout | Desktop: Side by side */}
        <div className="block lg:hidden">
          <Tabs defaultValue="profile">
            <TabsList className="bg-gray-50 border border-gray-200 mb-5 w-full">
              <TabsTrigger value="profile" className="flex-1 text-xs data-[state=active]:bg-[#FF2E00] data-[state=active]:text-white">Profile</TabsTrigger>
              <TabsTrigger value="notes" className="flex-1 text-xs data-[state=active]:bg-[#FF2E00] data-[state=active]:text-white">Notes & Actions</TabsTrigger>
              <TabsTrigger value="activity" className="flex-1 text-xs data-[state=active]:bg-[#FF2E00] data-[state=active]:text-white">Activity</TabsTrigger>
            </TabsList>
            <TabsContent value="profile"><ProfileCard lead={lead} statusValue={statusValue} onStatusChange={handleStatusChange} followUpDate={followUpDate} setFollowUpDate={setFollowUpDate} followUpTime={followUpTime} setFollowUpTime={setFollowUpTime} onSaveFollowUp={handleSaveFollowUp} onToggleBlacklist={handleToggleBlacklist} /></TabsContent>
            <TabsContent value="notes"><NotesSection editNotes={editNotes} setEditNotes={setEditNotes} onSave={handleSaveNotes} noteText={noteText} setNoteText={setNoteText} onAddNote={handleAddNote} addingNote={addingNote} inputClass={inputClass} /></TabsContent>
            <TabsContent value="activity"><ActivityTimeline log={activityLog} /></TabsContent>
          </Tabs>
        </div>

        <div className="hidden lg:grid grid-cols-12 gap-6">
          {/* Left — Profile + Status */}
          <div className="col-span-4 space-y-5">
            <ProfileCard lead={lead} statusValue={statusValue} onStatusChange={handleStatusChange} followUpDate={followUpDate} setFollowUpDate={setFollowUpDate} followUpTime={followUpTime} setFollowUpTime={setFollowUpTime} onSaveFollowUp={handleSaveFollowUp} onToggleBlacklist={handleToggleBlacklist} />
          </div>
          {/* Right — Notes + Activity */}
          <div className="col-span-8 space-y-5">
            <NotesSection editNotes={editNotes} setEditNotes={setEditNotes} onSave={handleSaveNotes} noteText={noteText} setNoteText={setNoteText} onAddNote={handleAddNote} addingNote={addingNote} inputClass={inputClass} />
            <ActivityTimeline log={activityLog} />
          </div>
        </div>
      </div>
    </div>
  );
}

function ProfileCard({ lead, statusValue, onStatusChange, followUpDate, setFollowUpDate, followUpTime, setFollowUpTime, onSaveFollowUp, onToggleBlacklist }) {
  return (
    <div className="bg-white border border-gray-200 p-5 space-y-4">
      {/* Name + Status */}
      <div>
        <div className="flex items-start justify-between gap-2">
          <h1 className="font-display text-2xl text-gray-900 tracking-wide">
            {lead.first_name} {lead.last_name}
          </h1>
          <button
            onClick={onToggleBlacklist}
            title={lead.blacklisted ? 'Remove from blacklist' : 'Add to blacklist'}
            data-testid="blacklist-toggle-btn"
            className={`flex items-center gap-1.5 text-[10px] font-bold px-2.5 py-1.5 rounded-lg border transition-all duration-150 shrink-0 ${
              lead.blacklisted
                ? 'bg-red-500/20 border-red-500/35 text-red-400 hover:bg-red-500/30'
                : 'bg-gray-50 border-gray-200 text-gray-400 hover:border-red-500/30 hover:text-red-400'
            }`}>
            {lead.blacklisted ? <><ShieldOff size={11} /> Blacklisted</> : <><ShieldCheck size={11} /> Blacklist</>}
          </button>
        </div>
        {lead.blacklisted && (
          <p className="text-red-400/70 text-xs mt-1 flex items-center gap-1">
            <ShieldOff size={10} /> This contact is blacklisted — no emails or SMS will be sent.
          </p>
        )}
        <StatusBadge status={lead.status} />
      </div>

      <Separator className="bg-gray-100" />

      {/* Contact */}
      <div className="space-y-2.5">
        {lead.phone && (
          <div className="flex items-center gap-2">
            <button onClick={() => gvCall(lead.phone)}
              data-testid="lead-call-btn"
              className="flex items-center gap-2 text-sm text-gray-700 hover:text-gray-900 transition-colors duration-200 flex-1">
              <Phone size={14} className="text-[#FF2E00] shrink-0" />{formatPhone(lead.phone)}
            </button>
            <button onClick={() => gvCall(lead.phone)}
              title="Call via Google Voice"
              className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-[#FF2E00]/15 border border-[#FF2E00]/25 text-[#FF8060] hover:bg-[#FF2E00]/30 transition-colors duration-150">
              Call
            </button>
            <button onClick={() => gvText(lead.phone)}
              title="Text via Google Voice"
              className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-purple-500/15 border border-purple-500/25 text-purple-300 hover:bg-purple-500/25 transition-colors duration-150">
              Text
            </button>
          </div>
        )}
        {lead.email && (
          <a href={`mailto:${lead.email}`} className="flex items-center gap-2.5 text-sm text-gray-700 hover:text-gray-900 transition-colors duration-200">
            <Mail size={14} className="text-[#FF2E00]" />{lead.email}
          </a>
        )}
      </div>

      <Separator className="bg-gray-100" />

      {/* Lead Info */}
      <dl className="space-y-2">
        {[
          { label: 'Interest', value: lead.interest_type },
          { label: 'Timeline', value: lead.start_timeline },
          { label: 'Contact Pref.', value: lead.preferred_contact },
          { label: 'Source', value: SOURCE_LABELS[lead.lead_source] || lead.lead_source },
          { label: 'Location', value: lead.location },
          { label: 'Created', value: lead.created_at ? new Date(lead.created_at).toLocaleDateString() : '—' },
        ].map((row) => (
          <div key={row.label} className="flex justify-between text-xs">
            <dt className="text-gray-700">{row.label}</dt>
            <dd className="text-gray-800 text-right">{row.value || '—'}</dd>
          </div>
        ))}
        {lead.training_goals && (
          <div className="pt-1">
            <dt className="text-gray-700 text-xs mb-1">Training Goals</dt>
            <dd className="text-gray-700 text-xs leading-relaxed">{lead.training_goals}</dd>
          </div>
        )}
      </dl>

      <Separator className="bg-gray-100" />

      {/* Status Change */}
      <div>
        <label className="block text-xs text-gray-700 mb-1.5">Update Status</label>
        <Select value={statusValue} onValueChange={onStatusChange}>
          <SelectTrigger className="w-full bg-gray-50 border-gray-200 text-gray-900 text-sm" data-testid="crm-lead-detail-status-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-white border-gray-200">
            {LEAD_STATUSES.map((s) => (
              <SelectItem key={s.value} value={s.value} className="text-gray-900 text-sm">{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Follow-up date + time */}
      <div>
        <label className="block text-xs text-gray-700 mb-1.5">Follow-up / Tour — Date & Time</label>
        <input
          type="date"
          value={followUpDate}
          onChange={(e) => setFollowUpDate(e.target.value)}
          className="w-full bg-gray-50 border border-gray-200 text-gray-900 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-300 mb-2"
          style={{ colorScheme: 'dark' }}
        />
        <TimeQuickSelect
          value={followUpTime}
          onChange={setFollowUpTime}
          selectedDate={followUpDate}
        />
        <button onClick={onSaveFollowUp} className="btn-scs-primary px-3 py-1.5 rounded-md text-xs w-full mt-2">
          Save Follow-up
        </button>
        {(lead.next_follow_up_date) && (
          <p className="text-gray-600 text-xs mt-1.5">
            Saved: {new Date(lead.next_follow_up_date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
            {lead.next_follow_up_time && ` at ${formatTime12(lead.next_follow_up_time)}`}
          </p>
        )}
      </div>

      {/* Tags */}
      {lead.tags && lead.tags.length > 0 && (
        <div>
          <p className="text-xs text-gray-700 mb-2">Tags</p>
          <div className="flex flex-wrap gap-1.5">
            {lead.tags.map((tag, i) => (
              <span key={i} className="text-xs bg-gray-100 text-gray-600 border border-gray-200 px-2 py-0.5 rounded-full">{tag}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function NotesSection({ editNotes, setEditNotes, onSave, noteText, setNoteText, onAddNote, addingNote, inputClass }) {
  return (
    <div className="bg-white border border-gray-200 p-5 space-y-4">
      {/* Quick Add Note */}
      <div>
        <h3 className="text-gray-900 text-sm font-semibold mb-2">Add a Note</h3>
        <textarea
          value={noteText}
          onChange={(e) => setNoteText(e.target.value)}
          placeholder="Log a call, text, email, or observation..."
          rows={2}
          data-testid="crm-lead-detail-add-note-textarea"
          className={`${inputClass} resize-none`}
        />
        <button
          onClick={onAddNote}
          disabled={addingNote || !noteText.trim()}
          data-testid="crm-lead-detail-add-note-submit-button"
          className="mt-2 btn-scs-primary px-4 py-2 rounded-md text-xs font-semibold flex items-center gap-1.5 disabled:opacity-50"
        >
          {addingNote ? <><Loader2 size={12} className="animate-spin" /> Adding...</> : <><MessageSquare size={12} /> Add Note</>}
        </button>
      </div>

      <Separator className="bg-gray-100" />

      {/* Long-form notes */}
      <div>
        <h3 className="text-gray-900 text-sm font-semibold mb-2">General Notes</h3>
        <textarea
          value={editNotes}
          onChange={(e) => setEditNotes(e.target.value)}
          placeholder="Running notes about this lead..."
          rows={4}
          className={`${inputClass} resize-none`}
        />
        <button
          onClick={onSave}
          className="mt-2 btn-scs-secondary px-4 py-2 rounded-md text-xs font-semibold"
        >
          Save Notes
        </button>
      </div>
    </div>
  );
}

function ActivityTimeline({ log }) {
  return (
    <div className="bg-white border border-gray-200 p-5" data-testid="crm-lead-detail-activity-timeline">
      <h3 className="text-gray-900 text-sm font-semibold mb-4">Activity Timeline</h3>
      {log.length === 0 ? (
        <p className="text-gray-600 text-sm">No activity yet.</p>
      ) : (
        <div className="space-y-3">
          {log.map((entry, i) => (
            <div key={i} className="flex gap-3">
              <div className="flex flex-col items-center">
                <div className="w-2 h-2 rounded-full bg-[#FF2E00] mt-1 shrink-0" />
                {i < log.length - 1 && <div className="w-0.5 flex-1 bg-gray-50 my-1" />}
              </div>
              <div className="pb-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-gray-900 text-xs font-medium">{entry.action}</span>
                  <span className="text-gray-500 text-xs">
                    {entry.timestamp ? new Date(entry.timestamp).toLocaleString() : ''}
                  </span>
                  {entry.staff_name && (
                    <span className="text-gray-500 text-xs">by {entry.staff_name}</span>
                  )}
                </div>
                {entry.note && (
                  <p className="text-gray-600 text-xs mt-0.5 leading-relaxed">{entry.note}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
